import { Injectable, inject, signal, computed, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TelemetryService } from '../../../services/telemetry.service';
import { PreferencesService } from '../../../services/preferences.service';
import { DeviceService } from '../../../services/device.service';
import { AuthService } from '../../../services/auth.service';
import { DashboardChartService } from './dashboard-chart.service';
import { take, delay } from 'rxjs/operators';
import { ChartConfiguration } from 'chart.js';

@Injectable({
  providedIn: 'root'
})
export class DashboardDataService {
  private telemetryService = inject(TelemetryService);
  private preferencesService = inject(PreferencesService);
  private deviceService = inject(DeviceService);
  private authService = inject(AuthService);
  private chartService = inject(DashboardChartService);
  private platformId = inject(PLATFORM_ID);

  // Writable Signals for State
  sessions = signal<any[]>([]);
  availableApps = signal<string[]>([]);
  loading = signal<boolean>(true);
  deviceCount = signal<number>(0);
  
  // Filter Signals
  selectedApp = signal<string>('all');
  selectedTimeRange = signal<string>('7d');
  customStartDate = signal<string>('');
  customEndDate = signal<string>('');

  // Computed KPIs
  crashCount = computed(() => this.sessions().filter(s => s.crashed || s.hasCrash || !!s.exception).length);
  
  crashRate = computed(() => {
    const total = this.sessions().length;
    if (total === 0) return '0';
    return ((this.crashCount() / total) * 100).toFixed(1);
  });

  globalAvgFps = computed(() => {
    const fpsData = this.sessions().map(s => this.chartService.getAvgFps(s)).filter(f => f > 0);
    if (fpsData.length === 0) return 0;
    return fpsData.reduce((a, b) => a + b, 0) / fpsData.length;
  });

  globalAvgApdex = computed(() => {
    const apdexData = this.sessions().map(s => s.apdexScore || 0).filter(a => a > 0);
    if (apdexData.length === 0) return 0;
    return apdexData.reduce((a, b) => a + b, 0) / apdexData.length;
  });

  // Computed Chart Data
  sortedSessions = computed(() => [...this.sessions()].reverse());
  
  chartLabels = computed(() => this.sortedSessions().map(s => {
    const d = new Date(s.backend_received_at);
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }));

  fpsChartData = computed(() => this.chartService.processFpsChartData(this.sortedSessions(), this.chartLabels()));
  cpuGpuChartData = computed(() => this.chartService.processCpuGpuChartData(this.sortedSessions(), this.chartLabels()));
  networkChartData = computed(() => this.chartService.processNetworkChartData(this.sortedSessions(), this.chartLabels()));
  ramFpsScatterData = computed(() => this.chartService.processRamFpsScatterData(this.sortedSessions()));
  versionStabilityChartData = computed(() => this.chartService.processVersionStabilityChartData(this.sortedSessions()));
  errorHealthData = computed(() => this.chartService.processErrorHealthData(this.sortedSessions()));
  errorTimelineData = computed(() => this.chartService.processErrorTimelineData(this.sortedSessions(), this.chartLabels()));
  errorFpsMixedData = computed(() => this.chartService.processErrorFpsMixedData(this.sortedSessions(), this.chartLabels()));
  apdexChartData = computed(() => this.chartService.processApdexChartData(this.sortedSessions(), this.chartLabels()));

  // Layout Management
  defaultWidgets = [
    { id: 'fps', title: 'Rendimiento Histórico (FPS)', type: 'line', dataSignal: this.fpsChartData, options: this.chartService.lineChartOptions },
    { id: 'cpuGpu', title: 'Nivel de Carga CPU / GPU', type: 'line', dataSignal: this.cpuGpuChartData, options: this.chartService.lineChartOptionsWithLegend },
    { id: 'errorHealth', title: 'Distribución de Salud de Sesiones', type: 'doughnut', dataSignal: this.errorHealthData, options: this.chartService.doughnutChartOptions },
    { id: 'errorTimeline', title: 'Histórico de Volumen de Crashes', type: 'bar', dataSignal: this.errorTimelineData, options: this.chartService.barChartOptionsWithLegend },
    { id: 'errorFps', title: 'Correlación: Crashes vs FPS', type: 'bar', dataSignal: this.errorFpsMixedData, options: this.chartService.mixedChartOptions, colSpan: 2 },
    { id: 'ramFps', title: 'Memoria vs FPS (Tamaño = Crashes)', type: 'bubble', dataSignal: this.ramFpsScatterData, options: this.chartService.bubbleChartOptions, colSpan: 2 },
    { id: 'network', title: 'Tráfico de Red (MB)', type: 'bar', dataSignal: this.networkChartData, options: this.chartService.barChartOptionsWithLegend },
    { id: 'apdex', title: 'Tendencia Apdex (Experiencia Web)', type: 'line', dataSignal: this.apdexChartData, options: this.chartService.apdexChartOptions },
    { id: 'stability', title: 'Estabilidad por Versión (Crashes vs Sesiones)', type: 'bar', dataSignal: this.versionStabilityChartData, options: this.chartService.stabilityChartOptions }
  ];

  widgets = signal<any[]>([...this.defaultWidgets]);

  constructor() {
    this.loadApps();
    this.loadDeviceCount();
    this.loadLayout();
    this.loadSessions(); // Initial load
  }

  updateFilters(app: string, range: string, start: string, end: string) {
    this.selectedApp.set(app);
    this.selectedTimeRange.set(range);
    this.customStartDate.set(start);
    this.customEndDate.set(end);
    this.loadSessions();
  }

  private calculateDates(): { startDate?: string, endDate?: string } {
    const range = this.selectedTimeRange();
    if (range === 'custom') {
      const start = this.customStartDate() ? new Date(this.customStartDate()).toISOString() : undefined;
      const end = this.customEndDate() ? new Date(this.customEndDate()) : undefined;
      if (end) end.setHours(23, 59, 59, 999);
      return { startDate: start, endDate: end?.toISOString() };
    }

    const end = new Date();
    const start = new Date();
    switch (range) {
      case '15m': start.setMinutes(start.getMinutes() - 15); break;
      case '30m': start.setMinutes(start.getMinutes() - 30); break;
      case '1h': start.setHours(start.getHours() - 1); break;
      case '24h': start.setHours(start.getHours() - 24); break;
      case '7d': start.setDate(start.getDate() - 7); break;
      default: return {};
    }
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }

  loadSessions() {
    this.loading.set(true);
    const { startDate, endDate } = this.calculateDates();
    const appFilter = this.selectedApp() === 'all' ? undefined : this.selectedApp();

    const stream = this.telemetryService.getSessions(1, 100, appFilter, startDate, endDate);
    const delayedStream = isPlatformBrowser(this.platformId) ? stream.pipe(delay(2000)) : stream;
    
    delayedStream.subscribe({
      next: (response) => {
        if (Array.isArray(response)) {
          this.sessions.set(response || []);
        } else {
          this.sessions.set(response.data || []);
        }
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading sessions', err);
        this.loading.set(false);
      }
    });
  }

  private loadApps() {
    this.telemetryService.getApps().subscribe({
      next: (apps) => this.availableApps.set(apps),
      error: (err) => console.error('Error loading apps:', err)
    });
  }

  private loadDeviceCount() {
    this.deviceService.getDevices().subscribe({
      next: (res) => this.deviceCount.set(res.devices?.length || 0),
      error: () => this.deviceCount.set(0)
    });
  }

  private loadLayout() {
    if (isPlatformBrowser(this.platformId)) {
      this.authService.user$.pipe(take(1)).subscribe(async (u: any) => {
        if (u) {
          try {
            this.preferencesService.getPreferences().subscribe({
              next: (prefs) => {
                if (prefs && prefs.dashboardLayout) {
                  const savedLayout = prefs.dashboardLayout;
                  const sorted = [...this.defaultWidgets].sort((a, b) => {
                    const idxA = savedLayout.indexOf(a.id);
                    const idxB = savedLayout.indexOf(b.id);
                    if (idxA === -1 && idxB === -1) return 0;
                    if (idxA === -1) return 1;
                    if (idxB === -1) return -1;
                    return idxA - idxB;
                  });
                  this.widgets.set(sorted);
                }
              },
              error: (e) => console.error("Error loading layout", e)
            });
          } catch (e) {
            console.error("Error loading layout", e);
          }
        }
      });
    }
  }

  saveLayout(layout: string[]) {
    if (isPlatformBrowser(this.platformId)) {
      this.authService.user$.pipe(take(1)).subscribe(async (user: any) => {
        if (user) {
          try {
            this.preferencesService.updatePreferences({ dashboardLayout: layout }).subscribe({
              error: (e) => console.error("Error saving layout", e)
            });
          } catch(e) {
             console.error("Error saving layout", e);
          }
        }
      });
    }
  }
}
